import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

const isDevelopment = import.meta.env.DEV;

i18n
  // Load translation files from public/locales
  .use(Backend)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    lng: 'en', // Default language
    fallbackLng: 'en',
    debug: isDevelopment,

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },

    ns: ['translation', 'survey-response'], // Add survey-response namespace
    defaultNS: 'translation',

    resources: {
      en: {
        translation: {
          // Authentication
          auth: {
            login: 'Sign In',
            register: 'Sign Up',
            logout: 'Logout',
            email: 'Email',
            password: 'Password',
            confirmPassword: 'Confirm Password',
            name: 'Full Name',
            welcomeBack: 'Welcome Back',
            createAccount: 'Create Account',
            signingIn: 'Signing In...',
            creatingAccount: 'Creating Account...',
            loginSuccess: 'Login successful',
            registerSuccess: 'Account created successfully',
            invalidCredentials: 'Invalid email or password',
            emailRequired: 'Email is required',
            passwordRequired: 'Password is required',
            nameRequired: 'Name is required',
            invalidEmail: 'Invalid email address',
            passwordTooShort: 'Password must be at least 8 characters',
            passwordsNotMatch: 'Passwords do not match',
            alreadyHaveAccount: 'Already have an account?',
            dontHaveAccount: "Don't have an account?",
          },
          // Dashboard
          dashboard: {
            title: 'Dashboard',
            welcome: 'Welcome back, {{name}}!',
            userInfo: 'User Information',
            quickActions: 'Quick Actions',
            systemStatus: 'System Status',
            getStarted: 'Get Started',
            authentication: 'Authentication',
            apiConnection: 'API Connection',
            session: 'Session',
            active: 'Active',
            connected: 'Connected',
            valid: 'Valid',
            quickStats: {
              title: 'Quick Stats',
              allTime: 'All Time',
              viewAll: 'View all statistics',
              ctaTitle: 'View Insight',
              ctaDescription: 'Select a university to view comprehensive insights and metrics.',
              viewInsight: 'View Insight',
              error: 'Unable to load statistics. Please try again.',
            },
          },
          // Common
          common: {
            loading: 'Loading...',
            error: 'Error',
            success: 'Success',
            cancel: 'Cancel',
            save: 'Save',
            edit: 'Edit',
            delete: 'Delete',
            confirm: 'Confirm',
            yes: 'Yes',
            no: 'No',
          },
          // Insight
          insight: {
            title: 'Insight',
            description: 'View comprehensive insights and metrics for surveys and responses',
            pageTitle: 'Insight Dashboard',

            summary: {
              domains: 'Institutes',
              subjects: 'Subjects',
              surveys: 'Total Surveys',
              instances: 'Survey Instances',
              responses: 'Total Responses',
              completedResponses: 'Completed Responses',
              completionRate: 'Completion Rate',
              avgResponsesPerSurvey: 'Avg Responses/Survey',
            },

            tabs: {
              overview: 'Overview',
              institutes: 'Institutes',
              subjects: 'Subjects',
              surveys: 'Surveys',
              surveyInsight: 'Survey Insight',
              selectInstitute: 'Select an institute from the table below to view its activity',
              selectSubject: 'Select a subject from the table below to view its activity',
              selectSurvey: 'Select a survey from the table below to view its activity',
            },

            tables: {
              instituteName: 'Institute Name',
              subjectName: 'Subject Name',
              institute: 'Institute',
              surveyTitle: 'Survey Title',
              subject: 'Subject',
              allSubjects: 'All Subjects',
            },

            charts: {
              activityOverTime: 'Activity Over Time',
              domainBreakdown: 'Institute Breakdown',
              instancesByInstitute: 'Survey Instances by Institute',
              topSurveys: 'Top Surveys by Response Count',
              completionMetrics: 'Response Completion Metrics',
            },

            filters: {
              dateRange: 'Date Range',
              domain: 'Institute',
              subject: 'Subject',
              survey: 'Survey',
              granularity: 'Time Granularity',
              allDomains: 'All Institutes',
              allSubjects: 'All Subjects',
              allSurveys: 'All Surveys',
              day: 'Daily',
              week: 'Weekly',
              month: 'Monthly',
            },

            tenantSelector: {
              label: 'Select University',
              placeholder: 'Choose a university to view statistics',
              noTenants: 'No universities available',
              loading: 'Loading universities...',
              error: 'Failed to load universities',
              selectPrompt:
                'Please select a university from the dropdown above to view statistics.',
              contextError: 'Unable to determine your context. Please contact support.',
            },

            surveyInsight: {
              title: 'Survey Insight',
              description: 'Select a survey template to view answer insights for all questions.',
              selectTemplate: 'Select Template',
              templatePlaceholder: 'Search templates...',
              noSelection: 'Select a survey template to view insights',
              noSelectionHint:
                'Choose a survey template above to see answer distribution for every question.',
              noQuestions: 'This template has no questions',
              error: 'Failed to load survey insights. Please try again.',
            },

            loading: 'Loading insight data...',
            error: 'Failed to load insight data',
            errorDetails: 'Error details: {{message}}',
            noData: 'No insight data available for the selected filters',
            empty: 'No data to display',
            retry: 'Retry',
          },
        },
      },
      no: {
        translation: {
          // Basic Norwegian translations for fallback
          common: {
            loading: 'Laster...',
            error: 'Feil',
            success: 'Suksess',
            cancel: 'Avbryt',
            save: 'Lagre',
            edit: 'Rediger',
            delete: 'Slett',
            confirm: 'Bekreft',
            yes: 'Ja',
            no: 'Nei',
          },
          // Dashboard
          dashboard: {
            quickStats: {
              title: 'Hurtigstatistikk',
              allTime: 'All tid',
              viewAll: 'Se all statistikk',
              ctaTitle: 'Se Innsikt',
              ctaDescription: 'Velg et universitet for å se omfattende innsikt og målinger.',
              viewInsight: 'Se Innsikt',
              error: 'Kunne ikke laste statistikk. Prøv igjen.',
            },
          },
          // Insight
          insight: {
            title: 'Innsikt',
            description: 'Vis omfattende innsikt og målinger for undersøkelser og svar',
            pageTitle: 'Innsiktsoversikt',

            summary: {
              domains: 'Institutter',
              subjects: 'Emner',
              surveys: 'Totalt Antall Undersøkelser',
              instances: 'Undersøkelsesinstanser',
              responses: 'Totalt Antall Svar',
              completedResponses: 'Fullførte Svar',
              completionRate: 'Fullføringsgrad',
              avgResponsesPerSurvey: 'Gj.snitt Svar/Undersøkelse',
            },

            tabs: {
              overview: 'Oversikt',
              institutes: 'Institutter',
              subjects: 'Emner',
              surveys: 'Undersøkelser',
              surveyInsight: 'Undersøkelsesinnsikt',
              selectInstitute: 'Velg et institutt fra tabellen nedenfor for å se aktiviteten',
              selectSubject: 'Velg et emne fra tabellen nedenfor for å se aktiviteten',
              selectSurvey: 'Velg en undersøkelse fra tabellen nedenfor for å se aktiviteten',
            },

            tables: {
              instituteName: 'Instituttnavn',
              subjectName: 'Emnenavn',
              institute: 'Institutt',
              surveyTitle: 'Undersøkelsestittel',
              subject: 'Emne',
              allSubjects: 'Alle Emner',
            },

            charts: {
              activityOverTime: 'Aktivitet Over Tid',
              domainBreakdown: 'Institutt-fordeling',
              instancesByInstitute: 'Undersøkelsesinstanser per Institutt',
              topSurveys: 'Topp Undersøkelser etter Antall Svar',
              completionMetrics: 'Fullføringsmålinger for Svar',
            },

            filters: {
              dateRange: 'Datoperiode',
              domain: 'Institutt',
              subject: 'Emne',
              survey: 'Undersøkelse',
              granularity: 'Tidsgranularitet',
              allDomains: 'Alle Institutter',
              allSubjects: 'Alle Emner',
              allSurveys: 'Alle Undersøkelser',
              day: 'Daglig',
              week: 'Ukentlig',
              month: 'Månedlig',
            },

            tenantSelector: {
              label: 'Velg Universitet',
              placeholder: 'Velg et universitet for å se statistikk',
              noTenants: 'Ingen universiteter tilgjengelig',
              loading: 'Laster universiteter...',
              error: 'Kunne ikke laste universiteter',
              selectPrompt:
                'Vennligst velg et universitet fra rullegardinmenyen ovenfor for å se statistikk.',
              contextError: 'Kan ikke fastslå konteksten din. Vennligst kontakt support.',
            },

            surveyInsight: {
              title: 'Undersøkelsesinnsikt',
              description: 'Velg en undersøkelsesmal for å se svarinnsikt for alle spørsmål.',
              selectTemplate: 'Velg Mal',
              templatePlaceholder: 'Søk maler...',
              noSelection: 'Velg en undersøkelsesmal for å se innsikt',
              noSelectionHint:
                'Velg en undersøkelsesmal ovenfor for å se svarfordeling for hvert spørsmål.',
              noQuestions: 'Denne malen har ingen spørsmål',
              error: 'Kunne ikke laste undersøkelsesinnsikt. Prøv igjen.',
            },

            loading: 'Laster innsiktsdata...',
            error: 'Kunne ikke laste innsiktsdata',
            errorDetails: 'Feildetaljer: {{message}}',
            noData: 'Ingen innsiktsdata tilgjengelig for de valgte filtrene',
            empty: 'Ingen data å vise',
            retry: 'Prøv igjen',
          },
        },
      },
    },
  });

export default i18n;
